import { useState, useCallback, useEffect } from 'react';
import { marketAPI, scenariosAPI } from '../lib/api';
import { useProject } from '../lib/project-context';
import { RequiresProject } from '../components/RequiresContext';
import { useComparables, useCreateComparable, useDeleteComparable, useMarketAnalysis, useMarketDerived } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  TrendingUp, Plus, Trash2, AlertTriangle, CheckCircle2, Info,
  BarChart3, Target, ArrowUpRight, ArrowDownRight, Loader2, Building2, MapPin, Users,
  DollarSign, Zap, ShieldAlert, FileText, Sparkles, Check, Minus, Link2 as LinkIcon, Pencil,
  Map as MapIcon, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { UpsellCard } from '../components/ModuleSystem';
import { ContextualNudge } from '../components/Nudges';
import { ModuleHeader } from '../components/ModuleHeader';
import { MarketMap } from '../components/MarketMap';
import { RadiusAnalysisPanel } from '../components/RadiusAnalysisPanel';
import { CrossValidationPanel } from '../components/CrossValidationPanel';

function fmt(n) { return n != null ? new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n) : '-'; }
function fmtD(n, d = 1) { return n != null ? Number(n).toFixed(d) : '-'; }

const INS = {
  positive: { icon: CheckCircle2, color: 'var(--success)', bg: 'var(--success-soft)' },
  warning: { icon: AlertTriangle, color: 'var(--warning-color)', bg: 'var(--warning-soft)' },
  info: { icon: Info, color: 'var(--info)', bg: 'var(--info-soft)' },
};
const RISK_COLOR = { bajo: 'var(--success)', medio: 'var(--warning-color)', alto: 'var(--danger)' };
const POS_COLOR = { premium: '#9333EA', above_market: 'var(--warning-color)', competitive: 'var(--success)', value: 'var(--market-primary)', aggressive: 'var(--danger)' };

/* ─── KPI Card ─── */
function KPI({ label, value, sub }) {
  return (
    <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-lg font-display font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}

/* ─── Insight Card ─── */
function InsightCard({ type, title, content }) {
  const cfg = INS[type] || INS.info;
  const Icon = cfg.icon;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-secondary)' }}>
      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: cfg.bg }}><Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} /></div>
      <div><div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</div><div className="text-[11px] leading-relaxed mt-0.5" style={{ color: 'var(--text-secondary)' }}>{content}</div></div>
    </div>
  );
}

/* ─── Market Score Card ─── */
function MarketScoreCard({ score }) {
  if (!score) return null;
  const total = score.total;
  const color = total >= 75 ? 'var(--success)' : total >= 55 ? 'var(--market-primary)' : total >= 35 ? 'var(--warning-color)' : 'var(--danger)';
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (total / 100) * circumference;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid="market-score-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-5">
          {/* Circular gauge */}
          <div className="relative shrink-0" style={{ width: 100, height: 100 }}>
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="var(--bg-hover)" strokeWidth="6" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
                strokeDasharray={circumference} strokeDashoffset={offset}
                transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-display font-bold tabular-nums" style={{ color }}>{total}</span>
              <span className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>/100</span>
            </div>
          </div>
          {/* Label + breakdown */}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-display font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'var(--text-muted)' }}>Market Score</div>
            <div className="text-lg font-display font-bold mb-3" style={{ color }}>{score.label}</div>
            <div className="space-y-1.5">
              {score.components.map(c => (
                <div key={c.key} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-secondary)' }}>{c.label}</span>
                      <span className="text-[10px] font-semibold tabular-nums" style={{ color: c.score >= 70 ? 'var(--success)' : c.score >= 45 ? 'var(--warning-color)' : 'var(--danger)' }}>{c.score}</span>
                    </div>
                    <div className="h-1 rounded-full" style={{ background: 'var(--bg-hover)' }}>
                      <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: c.score >= 70 ? 'var(--success)' : c.score >= 45 ? 'var(--warning-color)' : 'var(--danger)', transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                  <span className="text-[9px] tabular-nums shrink-0 w-8 text-right" style={{ color: 'var(--text-muted)' }}>{c.weight}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 flex flex-wrap gap-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
          {score.components.map(c => (
            <span key={c.key} className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)' }}>{c.detail}</span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Quick Add — Always visible, compact ─── */

/* ─── Source Badge ─── */
function SourceBadge({ source, confidence }) {
  if (!source || source === 'none' || confidence === 'none') {
    return <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>Sin conexión</span>;
  }
  const isApi = source.includes('inegi') || source.includes('denue') || source.includes('sniiv');
  const isCache = source.includes('cache');
  const isRef = source.includes('reference');

  if (isApi && !isCache) {
    return <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>INEGI en vivo</span>;
  }
  if (isApi && isCache) {
    return <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>INEGI (cache)</span>;
  }
  if (isRef && source.includes('proximity')) {
    return <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--warning-soft)', color: 'var(--warning-color)' }}>Referencia aproximada</span>;
  }
  return <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>Referencia</span>;
}

/* ─── Indicator Score Card ─── */
function IndicatorCard({ title, score, label, detail, isReference }) {
  const color = score == null ? 'var(--text-muted)' : score >= 70 ? 'var(--success)' : score >= 40 ? 'var(--warning-color)' : 'var(--danger)';
  return (
    <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{title}</span>
        {isReference && <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>Referencia</span>}
      </div>
      {score != null ? (
        <>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-2xl font-display font-bold tabular-nums" style={{ color }}>{score}</span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>/100</span>
          </div>
          <div className="h-1.5 rounded-full mb-2" style={{ background: 'var(--bg-hover)' }}>
            <div className="h-full rounded-full" style={{ width: `${score}%`, background: color, transition: 'width 0.5s' }} />
          </div>
          <span className="text-xs font-medium" style={{ color }}>{label}</span>
        </>
      ) : (
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{label || 'Sin datos'}</span>
      )}
      {detail && <p className="text-[10px] mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{detail}</p>}
    </div>
  );
}

/* ─── Context Panel ─── */
function ContextPanel({ contextData, derivedData, projectId, scenarioId, comparables = [] }) {
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const handleGenerateInsights = async () => {
    setLoadingAI(true);
    try {
      const res = await marketAPI.generateAIInsights(projectId, scenarioId);
      setAiInsights(res.data);
    } catch { toast.error('Error al generar insights'); }
    setLoadingAI(false);
  };

  const geo = contextData?.geo;
  const dem = contextData?.demographics || {};
  const eco = contextData?.economic || {};
  const hou = contextData?.housing || {};
  const meta = contextData?._meta || {};
  const indicators = derivedData?.indicators || {};

  const TYPE_STYLE = {
    pattern: { icon: BarChart3, color: 'var(--info)', bg: 'var(--info-soft)' },
    alert: { icon: AlertTriangle, color: 'var(--warning-color)', bg: 'var(--warning-soft)' },
    opportunity: { icon: TrendingUp, color: 'var(--success)', bg: 'var(--success-soft)' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Contexto de mercado</h2>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Información del entorno del proyecto basada en datos geográficos y comparables.</p>
      </div>

      {/* Geo status */}
      {geo && (
        <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: geo.geocoded ? 'var(--success-soft)' : 'var(--warning-soft)', border: `1px solid ${geo.geocoded ? 'rgba(12,191,115,0.15)' : 'rgba(245,166,35,0.15)'}` }}>
          <MapPin className="w-4 h-4 shrink-0" style={{ color: geo.geocoded ? 'var(--success)' : 'var(--warning-color)' }} />
          <div className="flex-1">
            <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
              {geo.city || geo.municipality || 'Ubicación parcial'}{geo.state ? `, ${geo.state}` : ''}{geo.country ? ` — ${geo.country}` : ''}
            </span>
            {geo.lat && <span className="text-[10px] ml-2" style={{ color: 'var(--text-muted)' }}>({geo.lat.toFixed(4)}, {geo.lon.toFixed(4)})</span>}
          </div>
          {geo.geocoded && <CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'var(--success)' }} />}
        </div>
      )}

      {/* Map with DENUE-informed radii (project + comparables + radios 1/3/5 km) */}
      {geo?.lat && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <MapIcon className="w-3.5 h-3.5" style={{ color: 'var(--market-primary)' }} />
            <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Mapa del entorno
            </h3>
          </div>
          <MarketMap projectCoords={geo} comparables={comparables} height={320} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Data sources */}
        <div className="lg:col-span-7 space-y-4">
          {/* Demographics */}
          <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Demografía</h3>
              <SourceBadge source={meta?.sources?.demographics?.source} confidence={meta?.sources?.demographics?.confidence} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <DataPoint label="Población" value={dem.population ? fmt(dem.population) : null} />
              <DataPoint label="Densidad" value={dem.density ? `${fmt(dem.density)} hab/km²` : null} />
              <DataPoint label="Zona metropolitana" value={dem.metro_population ? fmt(dem.metro_population) : null} />
            </div>
            {dem.source_detail && <p className="text-[9px] mt-2" style={{ color: 'var(--text-muted)' }}>{dem.source_detail}</p>}
          </div>

          {/* Economic Activity */}
          <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Actividad económica</h3>
              <SourceBadge source={meta?.sources?.economic?.source} confidence={meta?.sources?.economic?.confidence} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <DataPoint label="Establecimientos" value={eco.total_establishments ? fmt(eco.total_establishments) : null} />
              <DataPoint label="Índice actividad" value={eco.economic_activity_index != null ? `${fmtD(eco.economic_activity_index, 0)}/100` : null} sub={eco.vs_benchmark} />
              <DataPoint label="Densidad" value={eco.establishment_density ? `${fmtD(eco.establishment_density, 0)}/km²` : null} />
            </div>
            {eco.top_sectors && Object.keys(eco.top_sectors).length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <span className="text-[9px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Top sectores</span>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Object.entries(eco.top_sectors).slice(0, 5).map(([name, count]) => (
                    <span key={name} className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                      {name.slice(0, 35)} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}
            {eco.source_detail && <p className="text-[9px] mt-2" style={{ color: 'var(--text-muted)' }}>{eco.source_detail}</p>}
          </div>

          {/* Housing */}
          <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Vivienda</h3>
              <SourceBadge source={meta?.sources?.housing?.source} confidence={meta?.sources?.housing?.confidence} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <DataPoint label="Oferta" value={hou.housing_supply ? fmt(hou.housing_supply) : null} sub="uds registradas" />
              <DataPoint label="Demanda" value={hou.housing_demand ? fmt(hou.housing_demand) : null} sub="solicitudes" />
              <DataPoint label="Créditos" value={hou.housing_credits ? fmt(hou.housing_credits) : null} sub="otorgados" />
              <DataPoint label="Vivienda nueva" value={hou.new_housing ? fmt(hou.new_housing) : null} sub="unidades" />
            </div>
            {hou.supply_demand_ratio != null && (
              <div className="mt-3 pt-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>Oferta/Demanda:</span>
                  <span className="text-xs font-semibold" style={{ color: hou.supply_demand_ratio > 80 ? 'var(--success)' : hou.supply_demand_ratio > 50 ? 'var(--warning-color)' : 'var(--danger)' }}>
                    {hou.supply_demand_ratio}%
                  </span>
                  <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                    {hou.supply_demand_ratio > 80 ? '(oferta cubre demanda)' : hou.supply_demand_ratio > 50 ? '(déficit moderado)' : '(déficit alto)'}
                  </span>
                </div>
              </div>
            )}
            {hou._matched_to && <p className="text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>Referencia: {hou._matched_to}</p>}
            {hou.source_detail && <p className="text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>{hou.source_detail}</p>}
          </div>
        </div>

        {/* Right: Derived indicators */}
        <div className="lg:col-span-5 space-y-3">
          <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Indicadores derivados</h3>
          <IndicatorCard title="Densidad de competencia" {...indicators.competition_density} />
          <IndicatorCard title="Presión de mercado" {...indicators.market_pressure} />
          <IndicatorCard title="Actividad económica" {...indicators.economic_activity} isReference={indicators.economic_activity?.is_reference} />
          <IndicatorCard title="Alineación producto-zona" {...indicators.product_alignment} />
        </div>
      </div>

      {/* AI Insights */}
      <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Interpretación de mercado (IA)</h3>
          </div>
          <Button size="sm" variant="outline" onClick={handleGenerateInsights} disabled={loadingAI} className="text-xs h-7 gap-1.5" data-testid="generate-ai-insights">
            {loadingAI ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {loadingAI ? 'Analizando...' : aiInsights ? 'Regenerar' : 'Generar insights'}
          </Button>
        </div>
        {aiInsights?.insights ? (
          <div className="space-y-2">
            {aiInsights.insights.map((ins, i) => {
              const style = TYPE_STYLE[ins.type] || TYPE_STYLE.pattern;
              const Icon = style.icon;
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-primary)' }}>
                  <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: style.bg }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: style.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{ins.title}</span>
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>{ins.type === 'pattern' ? 'Patrón' : ins.type === 'alert' ? 'Alerta' : 'Oportunidad'}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{ins.content}</p>
                    {ins.action && <p className="text-[10px] mt-1 font-medium" style={{ color: style.color }}>{ins.action}</p>}
                  </div>
                </div>
              );
            })}
            <p className="text-[9px] text-center pt-1" style={{ color: 'var(--text-muted)' }}>{aiInsights.data_context}</p>
          </div>
        ) : (
          <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>Genera insights para obtener una interpretación automática de los datos de mercado.</p>
        )}
      </div>

      {/* Data sources footer */}
      {meta.all_reference && (
        <div className="rounded-lg p-3 text-center" style={{ background: 'var(--bg-hover)' }}>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            Datos de referencia. Configura <strong>INEGI_API_TOKEN</strong> para demografía y actividad económica en tiempo real.
          </p>
        </div>
      )}
    </div>
  );
}

function DataPoint({ label, value, sub }) {
  return (
    <div>
      <div className="text-[9px] font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-sm font-semibold tabular-nums mt-0.5" style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>{value || '—'}</div>
      {sub && <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</div>}
    </div>
  );
}


function QuickAdd({ projectId, onSaved }) {
  const [saving, setSaving] = useState(false);
  const empty = { name: '', location: '', avg_price_per_m2: '', avg_unit_price: '', total_units: '', absorption_units_month: '' };
  const [f, setF] = useState(empty);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!f.name.trim()) { toast.error('Dale un nombre al comparable'); return; }
    setSaving(true);
    try {
      await marketAPI.createComparable({
        project_id: projectId,
        name: f.name,
        location: f.location,
        avg_price_per_m2: parseFloat(f.avg_price_per_m2) || 0,
        avg_unit_price: parseFloat(f.avg_unit_price) || 0,
        total_units: parseInt(f.total_units) || 0,
        absorption_units_month: parseFloat(f.absorption_units_month) || 0,
      });
      toast.success('Comparable agregado');
      setF(empty);
      onSaved();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSave(); };

  return (
    <div className="rounded-xl p-4" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }} data-testid="quick-add-form">
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1 flex-1 min-w-[160px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Proyecto</Label>
          <Input value={f.name} onChange={e => set('name', e.target.value)} onKeyDown={handleKeyDown} placeholder="Nombre del proyecto" className="h-9" data-testid="comp-name" />
        </div>
        <div className="space-y-1 w-[130px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Ubicación</Label>
          <Input value={f.location} onChange={e => set('location', e.target.value)} onKeyDown={handleKeyDown} placeholder="Zona" className="h-9" />
        </div>
        <div className="space-y-1 w-[110px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Precio/m²</Label>
          <Input type="number" value={f.avg_price_per_m2} onChange={e => set('avg_price_per_m2', e.target.value)} onKeyDown={handleKeyDown} placeholder="$0" className="h-9 text-right tabular-nums" />
        </div>
        <div className="space-y-1 w-[80px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Unidades</Label>
          <Input type="number" value={f.total_units} onChange={e => set('total_units', e.target.value)} onKeyDown={handleKeyDown} placeholder="0" className="h-9 text-right tabular-nums" />
        </div>
        <div className="space-y-1 w-[90px]">
          <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Absorción</Label>
          <Input type="number" step="0.1" value={f.absorption_units_month} onChange={e => set('absorption_units_month', e.target.value)} onKeyDown={handleKeyDown} placeholder="uds/mes" className="h-9 text-right tabular-nums" />
        </div>
        <Button onClick={handleSave} disabled={saving || !f.name.trim()} className="h-9 text-white shrink-0" style={{ background: 'var(--market-primary)' }} data-testid="save-comparable-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</>}
        </Button>
      </div>
      <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>Usa estimaciones si no tienes el dato exacto. Puedes completar después.</p>
    </div>
  );
}

/* ─── AI Text Extractor — 3-step flow: Paste → Preview → Confirm ─── */
const CONF_STYLE = {
  high: { label: 'Alta', color: 'var(--success)', bg: 'var(--success-soft)', icon: Check },
  medium: { label: 'Media', color: 'var(--warning-color)', bg: 'var(--warning-soft)', icon: Minus },
  low: { label: 'Baja', color: 'var(--danger)', bg: 'var(--danger-soft)', icon: AlertTriangle },
};

const EXTRACT_FIELDS = [
  { key: 'name', label: 'Nombre del proyecto', type: 'text', required: true },
  { key: 'location', label: 'Ubicación', type: 'text' },
  { key: 'developer', label: 'Desarrollador', type: 'text' },
  { key: 'avg_unit_price', label: 'Precio por unidad', type: 'number' },
  { key: 'avg_price_per_m2', label: 'Precio por m²', type: 'number' },
  { key: 'avg_area_m2', label: 'm² promedio', type: 'number' },
  { key: 'total_units', label: 'Total unidades', type: 'number' },
  { key: 'units_sold', label: 'Vendidas', type: 'number' },
  { key: 'absorption_units_month', label: 'Absorción (uds/mes)', type: 'number' },
];

function TextExtractor({ projectId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0=input, 1=preview, 2=done
  const [mode, setMode] = useState('text'); // 'text' or 'url'
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [edited, setEdited] = useState({});
  const [saving, setSaving] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [sourcePreview, setSourcePreview] = useState('');

  const reset = () => { setStep(0); setText(''); setUrl(''); setExtracted(null); setEdited({}); setShowSource(false); setSourcePreview(''); };

  const handleExtractFromData = (data, preview) => {
    setExtracted(data);
    const vals = {};
    Object.entries(data).forEach(([k, v]) => { vals[k] = v?.value ?? ''; });
    setEdited(vals);
    setSourcePreview(preview || '');
    setStep(1);
  };

  const handleExtractText = async () => {
    if (text.trim().length < 20) { toast.error('Pega un texto más largo'); return; }
    setExtracting(true);
    try {
      const res = await marketAPI.extractFromText({ text, project_id: projectId });
      handleExtractFromData(res.data.extracted, text.slice(0, 300));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al extraer'); }
    setExtracting(false);
  };

  const handleExtractUrl = async () => {
    if (!url.trim().startsWith('http')) { toast.error('Ingresa una URL válida'); return; }
    setExtracting(true);
    try {
      const res = await marketAPI.extractFromUrl({ url: url.trim(), project_id: projectId });
      handleExtractFromData(res.data.extracted, res.data.source_text_preview || url);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo extraer. Intenta pegar el texto manualmente.'); setMode('text'); }
    setExtracting(false);
  };

  const handleSave = async () => {
    if (!edited.name) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      await marketAPI.createComparable({
        project_id: projectId, name: edited.name || '', location: edited.location || '',
        developer: edited.developer || '', avg_unit_price: parseFloat(edited.avg_unit_price) || 0,
        avg_price_per_m2: parseFloat(edited.avg_price_per_m2) || 0, avg_area_m2: parseFloat(edited.avg_area_m2) || 0,
        total_units: parseInt(edited.total_units) || 0, units_sold: parseInt(edited.units_sold) || 0,
        absorption_units_month: parseFloat(edited.absorption_units_month) || 0,
      });
      setStep(2);
      setTimeout(() => { setOpen(false); reset(); onSaved(); }, 1500);
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => { setOpen(true); reset(); }} className="gap-1.5" data-testid="open-extractor-btn">
        <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--accent-primary)' }} /> Pegar anuncio
      </Button>
    );
  }

  // Count detected fields
  const detectedCount = extracted ? Object.values(extracted).filter(v => v?.value != null).length : 0;

  return (
    <Card className="rounded-xl shadow-sm mb-5" style={{ border: '1px solid rgba(99,91,255,0.2)' }} data-testid="text-extractor">
      <CardContent className="p-5">
        {/* Header with steps */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <h4 className="text-sm font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Captura asistida</h4>
          </div>
          <div className="flex items-center gap-4">
            {/* Step indicators */}
            <div className="flex items-center gap-1">
              {['Pegar', 'Revisar', 'Listo'].map((s, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold" style={{
                    background: i < step ? 'var(--success)' : i === step ? 'var(--accent-primary)' : 'var(--bg-hover)',
                    color: i <= step ? '#fff' : 'var(--text-muted)',
                  }}>
                    {i < step ? <Check className="w-3 h-3" /> : i + 1}
                  </div>
                  <span className="text-[10px] font-medium hidden sm:inline" style={{ color: i === step ? 'var(--text-primary)' : 'var(--text-muted)' }}>{s}</span>
                  {i < 2 && <div className="w-4 h-px mx-0.5" style={{ background: i < step ? 'var(--success)' : 'var(--border-default)' }} />}
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setOpen(false); reset(); }} className="h-7 text-xs">Cerrar</Button>
          </div>
        </div>

        {/* STEP 0 — INPUT (text or URL) */}
        {step === 0 && (
          <div>
            {/* Mode toggle */}
            <div className="flex gap-1 mb-3 p-0.5 rounded-lg w-fit" style={{ background: 'var(--bg-hover)' }}>
              <button onClick={() => setMode('text')} className="px-3 py-1 rounded-md text-[11px] font-medium transition-colors" style={{ background: mode === 'text' ? 'var(--bg-secondary)' : 'transparent', color: mode === 'text' ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: mode === 'text' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>
                <FileText className="w-3 h-3 inline mr-1" />Pegar texto
              </button>
              <button onClick={() => setMode('url')} className="px-3 py-1 rounded-md text-[11px] font-medium transition-colors" style={{ background: mode === 'url' ? 'var(--bg-secondary)' : 'transparent', color: mode === 'url' ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: mode === 'url' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>
                <LinkIcon className="w-3 h-3 inline mr-1" />Pegar URL
              </button>
            </div>

            {mode === 'text' ? (
              <>
                <textarea
                  value={text} onChange={e => setText(e.target.value)}
                  placeholder={"Pega aquí el texto del anuncio inmobiliario...\n\nEjemplo: \"Preventa Torres del Lago en Zapopan. Departamentos de 85m² desde $3,200,000. 180 unidades, 60 vendidas.\""}
                  className="w-full h-32 rounded-lg p-3.5 text-sm resize-none leading-relaxed"
                  style={{ border: '1px solid var(--border-default)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  autoFocus data-testid="extractor-textarea"
                />
                <div className="flex items-center gap-3 mt-3">
                  <Button onClick={handleExtractText} disabled={extracting || text.trim().length < 20} className="text-white h-10 px-5" style={{ background: 'var(--accent-primary)' }} data-testid="extract-btn">
                    {extracting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analizando...</> : <><FileText className="w-4 h-4 mr-2" /> Analizar texto</>}
                  </Button>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Tú revisas y confirmas antes de guardar.</p>
                </div>
              </>
            ) : (
              <>
                <Input
                  value={url} onChange={e => setUrl(e.target.value)}
                  placeholder="https://www.inmuebles24.com/propiedad/..."
                  className="h-11 text-sm" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleExtractUrl(); }}
                  data-testid="extractor-url-input"
                />
                <p className="text-[10px] mt-1.5 mb-3" style={{ color: 'var(--text-muted)' }}>Pega el link de un anuncio de Inmuebles24, Vivanuncios, portal del desarrollador, o cualquier página con info del proyecto.</p>
                <div className="flex items-center gap-3">
                  <Button onClick={handleExtractUrl} disabled={extracting || !url.trim().startsWith('http')} className="text-white h-10 px-5" style={{ background: 'var(--accent-primary)' }} data-testid="extract-url-btn">
                    {extracting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Extrayendo de URL...</> : <><LinkIcon className="w-4 h-4 mr-2" /> Extraer de URL</>}
                  </Button>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Si falla, puedes copiar el texto y pegarlo manualmente.</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 1 — PREVIEW & EDIT */}
        {step === 1 && extracted && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Badge className="text-[10px]" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: 'none' }}>
                {detectedCount} de {EXTRACT_FIELDS.length} campos detectados
              </Badge>
              <button onClick={() => setShowSource(!showSource)} className="text-[10px] font-medium transition-colors hover:underline" style={{ color: 'var(--accent-primary)' }}>
                {showSource ? 'Ocultar texto original' : 'Ver texto original'}
              </button>
            </div>

            {/* Collapsible source text */}
            {showSource && (
              <div className="rounded-lg p-3 mb-4 text-xs leading-relaxed max-h-24 overflow-y-auto" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                {sourcePreview || text}
              </div>
            )}

            {/* Editable fields grid */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {EXTRACT_FIELDS.map(f => {
                const raw = extracted[f.key];
                const conf = raw?.confidence;
                const detected = raw?.value != null;
                const cs = CONF_STYLE[conf] || {};
                const ConfIcon = cs.icon;
                return (
                  <div key={f.key} className="space-y-1">
                    <div className="flex items-center gap-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{f.label}{f.required ? ' *' : ''}</Label>
                      {detected && conf ? (
                        <span className="inline-flex items-center gap-0.5 text-[8px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: cs.bg, color: cs.color }}>
                          <ConfIcon className="w-2 h-2" />{cs.label}
                        </span>
                      ) : (
                        <span className="text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>No detectado</span>
                      )}
                    </div>
                    <Input
                      type={f.type}
                      value={edited[f.key] ?? ''}
                      onChange={e => setEdited(p => ({ ...p, [f.key]: e.target.value }))}
                      className={`h-9 text-sm ${f.type === 'number' ? 'text-right tabular-nums' : ''}`}
                      style={{ borderColor: detected ? `${cs.color}30` : 'var(--border-default)', background: detected ? 'var(--bg-secondary)' : 'var(--bg-primary)' }}
                      placeholder={detected ? '' : 'Ingresa manualmente'}
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={handleSave} disabled={saving || !edited.name} className="text-white h-10 px-5" style={{ background: 'var(--market-primary)' }} data-testid="confirm-extract-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Guardar comparable
              </Button>
              <Button variant="outline" onClick={() => { setStep(0); setExtracted(null); }} className="h-10">Volver a pegar</Button>
              <p className="text-[10px] ml-auto" style={{ color: 'var(--text-muted)' }}>Edita cualquier campo antes de guardar</p>
            </div>
          </div>
        )}

        {/* STEP 2 — DONE */}
        {step === 2 && (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--success-soft)' }}>
              <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--success)' }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Comparable guardado</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Los insights se actualizarán automáticamente</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Simulation Panel with sliders ─── */
function SimulationPanel({ projectId, scenarioId, basePrice, baseAbsorption }) {
  const [priceDelta, setPriceDelta] = useState(0);
  const [absorption, setAbsorption] = useState(baseAbsorption);
  const [result, setResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => { setAbsorption(baseAbsorption); }, [baseAbsorption]);

  useEffect(() => {
    if (!projectId || !scenarioId) return;
    const t = setTimeout(async () => {
      setSimLoading(true);
      try {
        const res = await marketAPI.simulate({ project_id: projectId, scenario_id: scenarioId, price_delta_pct: priceDelta, absorption_override: absorption });
        setResult(res.data);
      } catch (err) { console.warn('[MarketIntelligence] operation failed', err); }
      setSimLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [priceDelta, absorption, projectId, scenarioId]);

  const sim = result?.simulated;
  const base = result?.base;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid="simulation-panel">
      <CardContent className="p-5">
        <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Simulación interactiva</h3>
        <p className="text-[11px] mb-5" style={{ color: 'var(--text-muted)' }}>Ajusta precio y absorción para ver el impacto estimado</p>

        {/* Sliders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-xs font-medium">Variación de precio</Label>
              <span className="text-xs font-bold tabular-nums" style={{ color: priceDelta !== 0 ? 'var(--market-primary)' : 'var(--text-primary)' }}>{priceDelta > 0 ? '+' : ''}{priceDelta}%</span>
            </div>
            <input type="range" min={-20} max={20} step={1} value={priceDelta} onChange={e => setPriceDelta(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ background: `linear-gradient(to right, var(--market-primary) 0%, var(--market-primary) ${(priceDelta + 20) / 40 * 100}%, var(--bg-hover) ${(priceDelta + 20) / 40 * 100}%, var(--bg-hover) 100%)`, accentColor: 'var(--market-primary)' }}
              data-testid="price-slider"
            />
            <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
              <span>-20%</span><span>0%</span><span>+20%</span>
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-xs font-medium">Absorción (uds/mes)</Label>
              <span className="text-xs font-bold tabular-nums" style={{ color: absorption !== baseAbsorption ? 'var(--market-primary)' : 'var(--text-primary)' }}>{fmtD(absorption)}</span>
            </div>
            <input type="range" min={Math.max(0.1, baseAbsorption * 0.3)} max={baseAbsorption * 2.5} step={0.1} value={absorption} onChange={e => setAbsorption(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: 'var(--market-primary)' }}
              data-testid="absorption-slider"
            />
            <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
              <span>{fmtD(baseAbsorption * 0.3)}</span><span>{fmtD(baseAbsorption)} (actual)</span><span>{fmtD(baseAbsorption * 2.5)}</span>
            </div>
          </div>
        </div>

        {/* Results */}
        {sim && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-primary)' }}>
              <div className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Precio</div>
              <div className="text-sm font-bold tabular-nums" style={{ color: priceDelta !== 0 ? 'var(--market-primary)' : 'var(--text-primary)' }}>${fmt(sim.price)}</div>
              {base && <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Base: ${fmt(base.price)}</div>}
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-primary)' }}>
              <div className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>TIR estimada</div>
              <div className="text-sm font-bold tabular-nums" style={{ color: sim.est_irr && sim.est_irr > 15 ? 'var(--success)' : sim.est_irr && sim.est_irr > 0 ? 'var(--warning-color)' : 'var(--danger)' }}>
                {sim.est_irr != null ? `${fmtD(sim.est_irr)}%` : '—'}
              </div>
              {base?.irr && <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Base: {fmtD(base.irr)}%</div>}
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-primary)' }}>
              <div className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Meses para vender</div>
              <div className="text-sm font-bold tabular-nums" style={{ color: sim.fits_timeline ? 'var(--success)' : 'var(--danger)' }}>{fmt(sim.months_to_sell)}</div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Cronograma: {sim.timeline_months}m</div>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ background: 'var(--bg-primary)' }}>
              <div className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Utilidad estimada</div>
              <div className="text-sm font-bold tabular-nums">${fmt(sim.est_profit)}</div>
              {base?.profit && <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Base: ${fmt(base.profit)}</div>}
            </div>
          </div>
        )}
        <p className="text-[10px] mt-3 p-2 rounded" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
          Estimaciones basadas en aproximación lineal. Ejecuta el análisis completo en Financial Intelligence para resultados precisos.
        </p>
      </CardContent>
    </Card>
  );
}

/* ─── Main Page ─── */
export default function MarketIntelligencePage() {
  const { activeProject, activeScenario, projects } = useProject();
  const qc = useQueryClient();
  const pid = activeProject?.id;
  const sid = activeScenario?.id;

  // React Query
  const { data: comparables = [], isLoading: compsLoading } = useComparables(pid);
  const { data: analysis } = useMarketAnalysis(pid, sid);
  const { data: derivedData } = useMarketDerived(pid, sid);

  const [snapshot, setSnapshot] = useState(null);
  const loading = compsLoading && !snapshot;
  const [tab, setTab] = useState('overview');
  const [changedKeys, setChangedKeys] = useState([]);
  const [lastInsight, setLastInsight] = useState(null);
  const [editingComp, setEditingComp] = useState(null);
  const [contextData, setContextData] = useState(null);

  // Load snapshot and context (not easily queryable due to complex params)
  useState(() => {
    if (pid) {
      marketAPI.getSnapshot(pid, sid).then(r => setSnapshot(r.data)).catch(() => {});
      marketAPI.getLocationData(pid).then(r => setContextData(r.data)).catch(() => {});
    }
  });

  const invalidateMarketData = () => {
    qc.invalidateQueries({ queryKey: queryKeys.comparables(pid) });
    qc.invalidateQueries({ queryKey: queryKeys.marketAnalysis(pid, sid) });
    qc.invalidateQueries({ queryKey: queryKeys.marketDerived(pid, sid) });
    qc.invalidateQueries({ queryKey: queryKeys.marketSuggestions(pid, sid) });
    // Refresh snapshot
    marketAPI.getSnapshot(pid, sid).then(r => setSnapshot(r.data)).catch(() => {});
  };

  const handleComparableAdded = useCallback(() => {
    invalidateMarketData();
    toast.success('Comparable agregado. Análisis de mercado actualizado.', { duration: 3000 });
  }, [pid, sid]);

  const handleDelete = async (id) => {
    try { await marketAPI.deleteComparable(id); toast.success('Eliminado'); invalidateMarketData(); } catch { toast.error('Error'); }
  };

  const handleEditSave = async (id, data) => {
    try {
      await marketAPI.updateComparable(id, data);
      toast.success('Comparable actualizado');
      setEditingComp(null);
      invalidateMarketData();
    } catch { toast.error('Error al actualizar'); }
  };

  // Apply cross-validation suggestion → update scenario assumption
  const handleApplySuggestion = async (assumptionKey, value, label) => {
    if (!sid || !pid) { toast.error('Selecciona un escenario primero'); return; }
    try {
      await scenariosAPI.updateAssumptions(pid, sid, { [assumptionKey]: value });
      toast.success(`${label} actualizado en el escenario`, { description: `${assumptionKey} = ${value}` });
      qc.invalidateQueries({ queryKey: queryKeys.marketCrossValidation(pid, sid) });
      qc.invalidateQueries({ queryKey: queryKeys.marketAnalysis(pid, sid) });
      qc.invalidateQueries({ queryKey: queryKeys.marketDerived(pid, sid) });
      qc.invalidateQueries({ queryKey: queryKeys.marketSnapshot(pid, sid) });
      qc.invalidateQueries({ queryKey: queryKeys.scenario(pid, sid) });
      qc.invalidateQueries({ queryKey: queryKeys.structuredInputs(sid) });
      marketAPI.getSnapshot(pid, sid).then(r => setSnapshot(r.data)).catch(() => {});
    } catch (e) {
      toast.error('Error al aplicar sugerencia', { description: e?.response?.data?.detail || e.message });
    }
  };

  if (!projects || projects.length === 0) return <RequiresProject moduleName="Market Intelligence"><div /></RequiresProject>;
  if (!pid) return <EmptyState icon={<TrendingUp className="w-10 h-10" style={{ color: 'var(--market-primary)', opacity: 0.3 }} />} title="Selecciona un proyecto" sub="Elige un proyecto desde el Dashboard para analizar su mercado." />;

  const snap = snapshot?.snapshot;
  const comp = snapshot?.project_comparison;
  const insights = snapshot?.insights || [];
  const mScore = snapshot?.market_score;
  const pos = analysis?.positioning;
  const pricing = analysis?.pricing;
  const absorp = analysis?.absorption;
  const recs = analysis?.recommendations || [];

  return (
    <div className="h-full overflow-y-auto" data-testid="market-intelligence-page">
      <div className="px-6 lg:px-8 py-6 max-w-[1400px]">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header with project/scenario selectors */}
          <ModuleHeader title="Market Intelligence" subtitle="Validación de mercado basada en comparables reales">
            <Badge className="text-[10px]" style={{ background: 'var(--market-light)', color: 'var(--market-primary)', border: 'none' }}>
              <Users className="w-3 h-3 mr-1" /> {comparables.length} comparables
            </Badge>
          </ModuleHeader>

          {loading ? (
            <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: 'var(--bg-hover)' }} />)}</div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-5">
                <TabsTrigger value="overview" data-testid="tab-overview">Mercado</TabsTrigger>
                <TabsTrigger value="context" data-testid="tab-context">Contexto</TabsTrigger>
                <TabsTrigger value="radius" data-testid="tab-radius">Radio</TabsTrigger>
                {analysis?.has_data && <TabsTrigger value="cross-validation" data-testid="tab-cross-validation">Validación</TabsTrigger>}
                {analysis?.has_data && <TabsTrigger value="diagnosis" data-testid="tab-diagnosis">Posicionamiento</TabsTrigger>}
                {analysis?.has_data && <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>}
                {analysis?.has_data && <TabsTrigger value="absorption" data-testid="tab-absorption">Absorción</TabsTrigger>}
                <TabsTrigger value="comparables" data-testid="tab-comparables">Comparables</TabsTrigger>
              </TabsList>

              {/* ── TAB: OVERVIEW ── */}
              <TabsContent value="overview">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-8 space-y-6">
                    {snap ? (
                      <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          <KPI label="Precio/m² promedio" value={`$${fmt(snap.avg_price_per_m2)}`} sub={`$${fmt(snap.min_price_per_m2)} — $${fmt(snap.max_price_per_m2)}`} />
                          <KPI label="Precio/ud promedio" value={`$${fmt(snap.avg_unit_price)}`} />
                          <KPI label="Absorción promedio" value={`${fmtD(snap.avg_absorption)} uds/mes`} />
                          <KPI label="Inventario activo" value={`${fmt(snap.active_inventory)} uds`} sub={`${fmtD(snap.sell_through_pct, 0)}% vendido`} />
                        </div>
                        {mScore && <MarketScoreCard score={mScore} />}
                        {comp && (
                          <Card className="rounded-xl border-border/50 shadow-sm">
                            <CardContent className="p-5">
                              <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Tu Proyecto vs Mercado</h3>
                              {[
                                { l: 'Precio/unidad', pv: comp.project_price, mv: comp.market_avg_price, d: comp.price_diff_pct, u: '$' },
                                comp.price_m2_diff_pct != null && { l: 'Precio/m²', pv: comp.project_price_m2, mv: comp.market_avg_price_m2, d: comp.price_m2_diff_pct, u: '$' },
                                { l: 'Absorción', pv: comp.project_absorption, mv: comp.market_avg_absorption, d: comp.absorption_diff_pct, u: '' },
                              ].filter(Boolean).map((r, i) => (
                                <div key={i} className="flex items-center gap-4 py-2.5" style={{ borderBottom: i < 2 ? '1px solid var(--border-subtle)' : 'none' }}>
                                  <span className="w-28 text-xs font-medium shrink-0" style={{ color: 'var(--text-secondary)' }}>{r.l}</span>
                                  <span className="w-24 text-xs font-semibold text-right tabular-nums" style={{ color: 'var(--market-primary)' }}>{r.u}{fmt(r.pv)}</span>
                                  <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--bg-hover)' }}><div className="h-full rounded-full" style={{ width: `${Math.min(100, 50 + (r.d || 0) / 2)}%`, background: 'var(--market-primary)' }} /></div>
                                  <span className="w-24 text-xs tabular-nums" style={{ color: 'var(--text-secondary)' }}>{r.u}{fmt(r.mv)}</span>
                                  <Badge className="text-[10px] tabular-nums w-16 justify-center" style={{ background: Math.abs(r.d) > 15 ? 'var(--warning-soft)' : 'var(--success-soft)', color: Math.abs(r.d) > 15 ? 'var(--warning-color)' : 'var(--success)', border: 'none' }}>{r.d > 0 ? '+' : ''}{fmtD(r.d)}%</Badge>
                                </div>
                              ))}
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : (
                      <Card className="rounded-xl border-border/50"><CardContent className="py-12 text-center">
                        <BarChart3 className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--market-primary)', opacity: 0.2 }} />
                        <p className="text-sm font-medium mb-1">Sin datos de mercado</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Agrega comparables en la pestaña "Comparables"</p>
                      </CardContent></Card>
                    )}
                  </div>
                  <div className="lg:col-span-4 space-y-3">
                    <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Insights</h3>
                    <div className="text-[10px] px-2 py-1 rounded" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>Basados en {comparables.length} comparables</div>
                    {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
                    {!sid && comparables.length > 0 && (
                      <div className="text-center p-4 rounded-lg" style={{ border: '1px dashed var(--border-default)' }}>
                        <Target className="w-5 h-5 mx-auto mb-2" style={{ color: 'var(--market-primary)', opacity: 0.4 }} />
                        <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Selecciona un escenario para comparar vs mercado</p>
                      </div>
                    )}
                    <UpsellCard fromModule="market" toModule="construction" />
                  </div>
                </div>
              </TabsContent>

              {/* ── TAB: CONTEXT ── */}
              <TabsContent value="context">
                <ContextPanel contextData={contextData} derivedData={derivedData} projectId={pid} scenarioId={sid} comparables={comparables} />
              </TabsContent>

              {/* ── TAB: RADIUS ANALYSIS ── */}
              <TabsContent value="radius">
                <div className="space-y-6">
                  <div>
                    <h2 className="text-base font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Análisis por radio</h2>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      Comparables agrupados por distancia geográfica al proyecto. Usa el mapa para revisar la concentración de competencia.
                    </p>
                  </div>
                  <MarketMap
                    projectCoords={contextData?.geo}
                    comparables={comparables}
                    height={420}
                  />
                  <RadiusAnalysisPanel projectId={pid} />
                </div>
              </TabsContent>

              {/* ── TAB: CROSS-VALIDATION ── */}
              {analysis?.has_data && (
                <TabsContent value="cross-validation">
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck className="w-4 h-4" style={{ color: 'var(--market-primary)' }} />
                        <h2 className="text-base font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Validación cruzada de supuestos</h2>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                        Compara tus supuestos del escenario contra los comparables capturados y contra benchmarks externos (INEGI/DENUE/SNIIV). Aplica sugerencias con un clic.
                      </p>
                    </div>
                    <CrossValidationPanel projectId={pid} scenarioId={sid} onApplyToScenario={handleApplySuggestion} />
                  </div>
                </TabsContent>
              )}

              {analysis?.has_data && (
                <TabsContent value="diagnosis">
                  <div className="space-y-6">
                    {/* Header */}
                    <div>
                      <h2 className="text-base font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Posicionamiento de mercado</h2>
                      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Compara tu proyecto contra el mercado y valida tus supuestos.</p>
                    </div>

                    {/* Diagnosis Cards */}
                    {pos && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Price positioning */}
                        <Card className="rounded-xl border-border/50 shadow-sm">
                          <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <DollarSign className="w-4 h-4" style={{ color: 'var(--market-primary)' }} />
                              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Precio</span>
                            </div>
                            <div className="text-2xl font-display font-bold mb-1" style={{ color: POS_COLOR[pos.position] }}>{pos.label}</div>
                            <div className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>{pos.description}</div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>${fmt(pos.market_min_price_m2)}</span>
                              <div className="flex-1 h-2 rounded-full relative" style={{ background: 'var(--bg-hover)' }}>
                                <div className="absolute h-full rounded-full" style={{ left: '10%', right: '10%', background: 'var(--market-light)' }} />
                                <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ left: `${Math.min(90, Math.max(10, 50 + pos.price_diff_pct / 2))}%`, background: POS_COLOR[pos.position] }} />
                              </div>
                              <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>${fmt(pos.market_max_price_m2)}</span>
                            </div>
                            <div className="text-center text-[10px] mt-1.5" style={{ color: 'var(--text-muted)' }}>{pos.price_diff_pct > 0 ? '+' : ''}{fmtD(pos.price_diff_pct)}% vs mercado</div>
                          </CardContent>
                        </Card>

                        {/* Absorption */}
                        {absorp && (
                          <Card className="rounded-xl border-border/50 shadow-sm">
                            <CardContent className="p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <TrendingUp className="w-4 h-4" style={{ color: 'var(--market-primary)' }} />
                                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Absorción</span>
                              </div>
                              <div className="text-2xl font-display font-bold mb-1" style={{ color: absorp.diff_pct > 30 ? 'var(--warning-color)' : 'var(--success)' }}>
                                {absorp.diff_pct > 30 ? 'Optimista' : absorp.diff_pct < -20 ? 'Conservadora' : 'Realista'}
                              </div>
                              <div className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                                {absorp.project_absorption} uds/mes vs {absorp.market_avg_absorption} del mercado
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                                  <div className="text-sm font-bold tabular-nums">{fmtD(absorp.project_absorption)}</div>
                                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Tu supuesto</div>
                                </div>
                                <div className="p-2 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
                                  <div className="text-sm font-bold tabular-nums">{fmtD(absorp.market_avg_absorption)}</div>
                                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Mercado</div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Risk */}
                        <Card className="rounded-xl border-border/50 shadow-sm">
                          <CardContent className="p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <ShieldAlert className="w-4 h-4" style={{ color: 'var(--market-primary)' }} />
                              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Riesgo</span>
                            </div>
                            <div className="text-2xl font-display font-bold mb-1 capitalize" style={{ color: RISK_COLOR[pos.risk_level] }}>
                              {pos.risk_level}
                            </div>
                            <div className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                              Basado en posicionamiento de precio y absorción
                            </div>
                            <div className="flex gap-1">
                              {['bajo', 'medio', 'alto'].map(level => (
                                <div key={level} className="flex-1 h-2 rounded-full" style={{ background: level === pos.risk_level ? RISK_COLOR[level] : 'var(--bg-hover)' }} />
                              ))}
                            </div>
                            <div className="flex justify-between text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>
                              <span>Bajo</span><span>Medio</span><span>Alto</span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}

                    {/* Simulation + Recommendations side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      {/* Simulation */}
                      <div className="lg:col-span-8">
                        <SimulationPanel projectId={pid} scenarioId={sid} basePrice={pos?.project_price || 0} baseAbsorption={absorp?.project_absorption || 0} />
                      </div>
                      {/* Recommendations */}
                      <div className="lg:col-span-4 space-y-3">
                        <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Recomendaciones</h3>
                        {recs.map((r, i) => (
                          <Card key={i} className="rounded-xl border-border/50 shadow-sm" data-testid={`recommendation-${i}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-2">
                                <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 mt-0.5" style={{ background: r.priority === 'high' ? 'var(--danger-soft)' : r.priority === 'medium' ? 'var(--warning-soft)' : 'var(--success-soft)' }}>
                                  <ShieldAlert className="w-3 h-3" style={{ color: r.priority === 'high' ? 'var(--danger)' : r.priority === 'medium' ? 'var(--warning-color)' : 'var(--success)' }} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{r.title}</span>
                                    <Badge className="text-[9px]" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)', border: 'none' }}>{r.area}</Badge>
                                  </div>
                                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{r.action}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        <div className="text-[10px] p-2 rounded" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                          Basado en {comparables.length} comparables capturados
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              )}

              {/* ── TAB: PRICING ── */}
              {analysis?.has_data && (
                <TabsContent value="pricing">
                  {pricing && (
                    <div className="space-y-6">
                      <ContextualNudge nudgeKey="market:price_change_impact" />
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <KPI label="Tu precio actual" value={`$${fmt(pricing.current_price)}`} />
                        <KPI label="Rango recomendado" value={`$${fmt(pricing.recommended_min)} — $${fmt(pricing.recommended_max)}`} sub="Percentil 25-75 de comparables" />
                        <KPI label="Promedio de mercado" value={`$${fmt(pricing.optimal_price)}`} />
                        <KPI label="TIR base" value={pricing.base_irr != null ? `${fmtD(pricing.base_irr)}%` : 'Sin análisis'} />
                      </div>
                      <Card className="rounded-xl border-border/50 shadow-sm">
                        <CardContent className="p-5">
                          <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Simulación de Precio</h3>
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/20">
                                <TableHead className="text-xs">Variación</TableHead>
                                <TableHead className="text-xs text-right">Precio</TableHead>
                                <TableHead className="text-xs text-right">TIR estimada</TableHead>
                                <TableHead className="text-xs text-right">Absorción est.</TableHead>
                                <TableHead className="text-xs text-right">Meses venta</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pricing.scenarios.map((s, i) => (
                                <TableRow key={i} className={s.is_current ? 'bg-[var(--market-subtle)]' : ''}>
                                  <TableCell className="text-sm font-medium">{s.delta_pct > 0 ? '+' : ''}{s.delta_pct}%{s.is_current && <Badge className="ml-2 text-[9px]" style={{ background: 'var(--market-light)', color: 'var(--market-primary)', border: 'none' }}>Actual</Badge>}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">${fmt(s.price)}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{s.est_irr != null ? `${fmtD(s.est_irr)}%` : '-'}</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmtD(s.est_absorption)} uds/mes</TableCell>
                                  <TableCell className="text-sm text-right tabular-nums">{fmt(s.months_to_sell)} meses</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <p className="text-[10px] mt-3 p-2 rounded" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                            Estimaciones basadas en elasticidad de mercado y {pricing.data_source}. Las TIR estimadas son aproximaciones lineales.
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* ── TAB: ABSORPTION ── */}
              {analysis?.has_data && (
                <TabsContent value="absorption">
                  {absorp && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <KPI label="Tu absorción" value={`${fmtD(absorp.project_absorption)} uds/mes`} sub={absorp.diff_pct > 0 ? `${fmtD(absorp.diff_pct)}% sobre mercado` : `${fmtD(Math.abs(absorp.diff_pct))}% bajo mercado`} />
                        <KPI label="Mercado promedio" value={`${fmtD(absorp.market_avg_absorption)} uds/mes`} sub={absorp.data_source} />
                        <KPI label="Meses para vender" value={`${fmt(absorp.months_to_sell_current)} meses`} sub={absorp.months_to_sell_current <= absorp.project_timeline_months ? 'Dentro del cronograma' : 'Excede cronograma'} />
                        <KPI label="Cronograma del proyecto" value={`${absorp.project_timeline_months} meses`} />
                      </div>
                      <Card className="rounded-xl border-border/50 shadow-sm">
                        <CardContent className="p-5">
                          <h3 className="text-xs font-display font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--text-muted)' }}>Escenarios de Absorción</h3>
                          <div className="space-y-3">
                            {absorp.scenarios.map((s, i) => (
                              <div key={i} className={`flex items-center gap-4 p-3 rounded-lg ${s.is_current ? 'ring-1' : ''}`} style={{ background: s.is_current ? 'var(--market-subtle)' : 'var(--bg-primary)', borderColor: s.is_current ? 'var(--market-primary)' : undefined }}>
                                <div className="w-24 shrink-0">
                                  <div className="text-xs font-semibold" style={{ color: s.is_current ? 'var(--market-primary)' : 'var(--text-primary)' }}>{s.label}</div>
                                  {s.is_estimated && <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Estimado</div>}
                                </div>
                                <div className="text-sm font-semibold tabular-nums w-24">{fmtD(s.rate)} uds/mes</div>
                                <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--bg-hover)' }}>
                                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, (s.rate / (absorp.market_avg_absorption * 1.5)) * 100)}%`, background: s.fits_timeline ? 'var(--market-primary)' : 'var(--warning-color)' }} />
                                </div>
                                <div className="text-sm tabular-nums w-24 text-right">{fmt(s.months_to_sell)} meses</div>
                                <Badge className="text-[10px] w-20 justify-center" style={{ background: s.fits_timeline ? 'var(--success-soft)' : 'var(--warning-soft)', color: s.fits_timeline ? 'var(--success)' : 'var(--warning-color)', border: 'none' }}>
                                  {s.fits_timeline ? 'OK' : 'Excede'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                          <p className="text-[10px] mt-3 p-2 rounded" style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
                            Escenarios conservador/optimista calculados como ±30% del promedio de mercado. {absorp.data_source}.
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* ── TAB: COMPARABLES ── */}
              <TabsContent value="comparables">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left: Quick add + Table */}
                  <div className="lg:col-span-8 space-y-5">
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-base font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Comparables de mercado</h2>
                      <TextExtractor projectId={pid} onSaved={handleComparableAdded} />
                    </div>
                    <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Agrega proyectos similares para validar el posicionamiento de tu desarrollo.</p>

                    {/* Quick Add — always visible */}
                    <QuickAdd projectId={pid} onSaved={handleComparableAdded} />

                    {/* Table */}
                    {comparables.length > 0 ? (
                      <Card className="rounded-xl border-border/50 shadow-sm overflow-hidden">
                        <div className="overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/20">
                                <TableHead className="text-xs min-w-[160px]">Proyecto</TableHead>
                                <TableHead className="text-xs text-right w-[90px]">Precio/m²</TableHead>
                                <TableHead className="text-xs text-right w-[110px]">Precio/ud</TableHead>
                                <TableHead className="text-xs text-right w-[60px]">Uds</TableHead>
                                <TableHead className="text-xs text-right w-[70px]">Vendidas</TableHead>
                                <TableHead className="text-xs text-right w-[80px]">Absorción</TableHead>
                                <TableHead className="text-xs w-[40px]"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {comparables.map(c => (
                                editingComp === c.id ? (
                                  <EditRow key={c.id} comparable={c} onSave={(data) => handleEditSave(c.id, data)} onCancel={() => setEditingComp(null)} />
                                ) : (
                                <TableRow key={c.id} className="hover:bg-accent/20 group" data-testid={`comparable-row-${c.id}`}>
                                  <TableCell>
                                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</div>
                                    {c.location && <div className="text-[10px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><MapPin className="w-2.5 h-2.5" />{c.location}</div>}
                                  </TableCell>
                                  <TableCell className="text-right text-sm tabular-nums">{c.avg_price_per_m2 ? `$${fmt(c.avg_price_per_m2)}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</TableCell>
                                  <TableCell className="text-right text-sm tabular-nums">{c.avg_unit_price ? `$${fmt(c.avg_unit_price)}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</TableCell>
                                  <TableCell className="text-right text-sm tabular-nums">{c.total_units || <span style={{ color: 'var(--text-muted)' }}>—</span>}</TableCell>
                                  <TableCell className="text-right text-sm tabular-nums">
                                    {c.units_sold || 0}
                                    {c.total_units > 0 && <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>({fmtD(c.units_sold / c.total_units * 100, 0)}%)</span>}
                                  </TableCell>
                                  <TableCell className="text-right text-sm tabular-nums">{c.absorption_units_month ? fmtD(c.absorption_units_month) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => setEditingComp(c.id)} data-testid={`edit-comparable-${c.id}`}>
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(c.id)}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                )
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </Card>
                    ) : (
                      <div className="py-8 text-center rounded-xl" style={{ border: '2px dashed var(--border-default)' }}>
                        <Building2 className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
                        <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Aún no tienes comparables</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Usa el formulario de arriba para agregar el primer proyecto de tu zona</p>
                      </div>
                    )}
                  </div>

                  {/* Right: Live insights */}
                  <div className="lg:col-span-4 space-y-4">
                    <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Resumen en tiempo real</h3>

                    {/* Highlighted insight after adding comparable */}
                    {lastInsight && (
                      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'var(--accent-subtle)', border: '1px solid rgba(99,91,255,0.15)' }}>
                        {lastInsight.type === 'up' ? <ArrowUpRight className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} /> : <ArrowDownRight className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />}
                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{lastInsight.text}</span>
                      </motion.div>
                    )}

                    {comparables.length > 0 ? (
                      <>
                        {(() => {
                          const pm2 = comparables.filter(c => c.avg_price_per_m2 > 0).map(c => c.avg_price_per_m2);
                          const abs = comparables.filter(c => c.absorption_units_month > 0).map(c => c.absorption_units_month);
                          const inv = comparables.reduce((s, c) => s + (c.total_units || 0) - (c.units_sold || 0), 0);
                          const isChanged = (key) => changedKeys.includes(key);
                          const highlight = (key) => isChanged(key) ? { border: '1px solid var(--market-primary)', background: 'var(--market-subtle)', transition: 'all 0.5s ease' } : { border: '1px solid var(--border-default)', background: 'var(--bg-secondary)', transition: 'all 0.5s ease' };
                          return (
                            <div className="space-y-3">
                              {pm2.length > 0 && (
                                <div className="p-3 rounded-lg" style={highlight('price')}>
                                  <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isChanged('price') ? 'var(--market-primary)' : 'var(--text-muted)' }}>Precio/m²</div>
                                  <div className="text-lg font-display font-bold tabular-nums mt-1">${fmt(pm2.reduce((a, b) => a + b, 0) / pm2.length)}</div>
                                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>${fmt(Math.min(...pm2))} — ${fmt(Math.max(...pm2))}</div>
                                </div>
                              )}
                              {abs.length > 0 && (
                                <div className="p-3 rounded-lg" style={highlight('absorption')}>
                                  <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isChanged('absorption') ? 'var(--market-primary)' : 'var(--text-muted)' }}>Absorción promedio</div>
                                  <div className="text-lg font-display font-bold tabular-nums mt-1">{fmtD(abs.reduce((a, b) => a + b, 0) / abs.length)} uds/mes</div>
                                </div>
                              )}
                              <div className="p-3 rounded-lg" style={highlight('inventory')}>
                                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isChanged('inventory') ? 'var(--market-primary)' : 'var(--text-muted)' }}>Inventario disponible</div>
                                <div className="text-lg font-display font-bold tabular-nums mt-1">{fmt(inv)} uds</div>
                              </div>
                              <div className="p-3 rounded-lg" style={highlight('count')}>
                                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isChanged('count') ? 'var(--market-primary)' : 'var(--text-muted)' }}>Comparables capturados</div>
                                <div className="text-lg font-display font-bold tabular-nums mt-1">{comparables.length}</div>
                                <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{comparables.length < 3 ? 'Agrega más para mejor análisis' : 'Datos suficientes para diagnóstico'}</div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Cross-module suggestions */}
                        {comparables.length >= 2 && sid && (
                          <div className="rounded-lg p-3" style={{ background: 'var(--financial-subtle)', border: '1px solid rgba(99,91,255,0.1)' }}>
                            <div className="flex items-center gap-2 mb-1">
                              <DollarSign className="w-3.5 h-3.5" style={{ color: 'var(--financial-primary)' }} />
                              <span className="text-[10px] font-semibold" style={{ color: 'var(--financial-primary)' }}>Financial Intelligence</span>
                            </div>
                            <p className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>Ejecuta el análisis financiero para ver cómo tu posicionamiento de mercado impacta la TIR.</p>
                          </div>
                        )}

                        {insights.length > 0 && (
                          <>
                            <h3 className="text-xs font-display font-semibold uppercase tracking-wider pt-2" style={{ color: 'var(--text-muted)' }}>Insights</h3>
                            {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-center p-6 rounded-lg" style={{ border: '1px dashed var(--border-default)' }}>
                        <BarChart3 className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Los insights aparecerán aquí cuando agregues comparables</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </motion.div>
      </div>
    </div>
  );
}

/* ─── Inline Edit Row ─── */
function EditRow({ comparable, onSave, onCancel }) {
  const [f, setF] = useState({
    name: comparable.name || '',
    location: comparable.location || '',
    avg_price_per_m2: comparable.avg_price_per_m2 || '',
    avg_unit_price: comparable.avg_unit_price || '',
    total_units: comparable.total_units || '',
    units_sold: comparable.units_sold || '',
    absorption_units_month: comparable.absorption_units_month || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      name: f.name, location: f.location,
      avg_price_per_m2: parseFloat(f.avg_price_per_m2) || 0,
      avg_unit_price: parseFloat(f.avg_unit_price) || 0,
      total_units: parseInt(f.total_units) || 0,
      units_sold: parseInt(f.units_sold) || 0,
      absorption_units_month: parseFloat(f.absorption_units_month) || 0,
    });
    setSaving(false);
  };

  return (
    <TableRow className="bg-[var(--accent-subtle)]" data-testid={`edit-row-${comparable.id}`}>
      <TableCell>
        <Input value={f.name} onChange={e => set('name', e.target.value)} className="h-7 text-sm" autoFocus />
        <Input value={f.location} onChange={e => set('location', e.target.value)} className="h-7 text-sm mt-1" placeholder="Ubicación" />
      </TableCell>
      <TableCell><Input type="number" value={f.avg_price_per_m2} onChange={e => set('avg_price_per_m2', e.target.value)} className="h-7 text-sm text-right tabular-nums w-20" /></TableCell>
      <TableCell><Input type="number" value={f.avg_unit_price} onChange={e => set('avg_unit_price', e.target.value)} className="h-7 text-sm text-right tabular-nums w-24" /></TableCell>
      <TableCell><Input type="number" value={f.total_units} onChange={e => set('total_units', e.target.value)} className="h-7 text-sm text-right tabular-nums w-14" /></TableCell>
      <TableCell><Input type="number" value={f.units_sold} onChange={e => set('units_sold', e.target.value)} className="h-7 text-sm text-right tabular-nums w-16" /></TableCell>
      <TableCell><Input type="number" step="0.1" value={f.absorption_units_month} onChange={e => set('absorption_units_month', e.target.value)} className="h-7 text-sm text-right tabular-nums w-16" /></TableCell>
      <TableCell>
        <div className="flex gap-0.5">
          <Button size="icon" className="h-7 w-7 text-white" style={{ background: 'var(--market-primary)' }} onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={onCancel}>
            <Minus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}


function EmptyState({ icon, title, sub }) {
  return <div className="px-6 py-6 max-w-4xl mx-auto text-center pt-20"><div className="mb-4 flex justify-center">{icon}</div><h2 className="font-display text-lg font-semibold mb-1">{title}</h2><p className="text-sm" style={{ color: 'var(--text-muted)' }}>{sub}</p></div>;
}
